import classnames from 'classnames'
import { useRecipientResolution } from 'hooks/useRecipientResolution'
import parse, { attributesToProps, domToReact } from 'html-react-parser'
import { isEmptyMessage } from 'modules/message/utils'
import React, { useLayoutEffect, useMemo } from 'react'
import { LiveMessage } from 'react-aria-live'
import { MessageAuthorType, ViewerRecipientStatus } from 'types/message'
import { getNewMessageAriaLabel } from 'utils/a11y'
import { parseAndAddLinks } from 'utils/url'

import { MessageTypeProps } from './types'
interface HtmlMessageProps extends MessageTypeProps {
  className?: string,
}

const GIF_REGEX = /[/.](gif)$/i;

const HtmlMessage = ({
  body,
  attributes,
  isLastMessage,
  authorId,
  isLastGroup,
  style = {},
  resolveMessageHeight = () => { },
  className,
  viewerRecipientStatus,
  authorType
}: HtmlMessageProps) => {
  useLayoutEffect(() => {
    resolveMessageHeight()
  }, [resolveMessageHeight, body])

  const resolvedRecipient = useRecipientResolution(authorId)

  const { parsedBody } = useMemo(() => {
    const parsedBody = parse(body,{
      replace: (domNode) => {
        if(domNode['name'] === 'img'){
          const alt = domNode['attribs']?.alt ?? ''
          const src = domNode['attribs']?.src ?? ''

          const isGif = new RegExp(GIF_REGEX).test(src)

          const newAttribs = {
            ...attributesToProps(domNode['attribs']),
            onLoad:resolveMessageHeight
          }

          if (isGif) {
            newAttribs['data-giffer'] = src
            newAttribs['data-giffer-alt'] = alt
          }

          // the alt-text already exists in domNode.attribs
          // eslint-disable-next-line jsx-a11y/alt-text
          return <img {...newAttribs} />
        }
        else if (domNode['type'] === 'text'){
          if (domNode.parentNode && domNode.parentNode['name'] !== 'a'){
            const urlParsedContent = parseAndAddLinks(domNode['data'])

            return <>{parse(urlParsedContent)}</>
          }
        }
        else if (domNode['name'] === 'a') {
          const newAttribs = {
            href: domNode['attribs']['href'],
            target: '_blank',
            rel: 'noopener noreferrer'
          }
          return <a {...newAttribs}>{domToReact(domNode['children'])}</a>
        }
      }
    })

    return { parsedBody }
  },[body, resolveMessageHeight])

  const shouldAnnounceMessage = (
    isLastMessage &&
    isLastGroup &&
    authorType === MessageAuthorType.USER &&
    viewerRecipientStatus !== ViewerRecipientStatus.READ
  )

  const a11yMessage = shouldAnnounceMessage ? getNewMessageAriaLabel(
    resolvedRecipient.recipient.name,
    attributes && attributes.preview !== '' ? attributes.preview : body
    ) : null

  return (
    <div
      className={classnames(`drift-widget-message--box drift-widget-message--HTML`, className)}
      style={style}
    >
      {shouldAnnounceMessage &&
        <LiveMessage
          message={a11yMessage}
          aria-live="polite"
          clearOnUnmount
        />
      }
      <div>
        {parsedBody}
      </div>
    </div>
  )
}

export default (props: HtmlMessageProps) =>
  isEmptyMessage(props.body) ? null : <HtmlMessage {...props} />
